import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import {
  collection,
  getDocs,
  limit as fsLimit,
  query
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { applySort } from '../lib/sort';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { maskEmail, maskHandle } from '../lib/mask';

const USERS_FETCH_LIMIT = 2000;

function formatTs(n) {
  if (!n) return '—';
  const d = new Date(n);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Mapea providerId → etiqueta corta. Firebase devuelve 'google.com',
// 'password', 'apple.com', etc.
function providerLabel(p) {
  if (p === 'google.com') return 'Google';
  if (p === 'password') return 'Email';
  if (p === 'apple.com') return 'Apple';
  return p;
}

export default function Beta() {
  const navigate = useNavigate();
  const { isViewer, canWrite } = useAuth();
  const [betaRows, setBetaRows] = useState([]);
  const [users, setUsers] = useState([]); // [{id, email, tiktokUsername, plan, planEnd, ADMIN}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [viewFilter, setViewFilter] = useState('all');
  const [sortDescriptor, setSortDescriptor] = useState({
    column: 'approved_at',
    direction: 'descending'
  });

  const [lookupError, setLookupError] = useState(null);

  async function loadAll() {
    setLoading(true);
    setError(null);
    setLookupError(null);
    try {
      const [bRes, uSnap] = await Promise.all([
        apiFetch('/api/backoffice/beta'),
        getDocs(query(collection(db, 'users'), fsLimit(USERS_FETCH_LIMIT)))
      ]);
      setBetaRows(bRes.rows || []);
      if (bRes.lookupError) setLookupError(bRes.lookupError);
      setUsers(
        uSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            email: (data.email || '').toLowerCase(),
            tiktokUsername: data.tiktokUsername || null,
            plan: data.plan || 'free',
            planEnd: data.planEnd || null,
            ADMIN: data.ADMIN === true
          };
        })
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  // UID → user doc Firestore. Clave: uid. Firestore `users/{uid}` puede NO
  // tener `email` (flujo Google sign-in en la Electron no lo escribe),
  // por eso cruzamos por uid, no por email.
  const userByUid = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  // beta_emails ya vienen enriquecidos desde el worker con `uid` y
  // `providers` (cruce server-side contra Firebase Auth). El client solo
  // añade plan/tiktokUsername desde Firestore usando el uid.
  const enriched = useMemo(() => {
    return betaRows.map((b) => {
      const uid = b.uid || null;
      const u = uid ? userByUid.get(uid) : null;
      return {
        email: (b.email || '').toLowerCase(),
        approved_at: b.approved_at,
        welcome_sent: b.welcome_sent === 1,
        welcome_sent_at: b.welcome_sent_at,
        registered: !!uid,
        providers: b.providers || [],
        user: u
          ? u
          : uid
          ? { id: uid, email: b.email, tiktokUsername: null, plan: 'free', planEnd: null, ADMIN: false }
          : null
      };
    });
  }, [betaRows, userByUid]);

  // Usuarios Firestore que NO están en beta_emails (entraron fuera de la
  // beta o se registraron antes de la tabla). Cruzamos por uid porque el
  // email en Firestore puede faltar (Google sign-in).
  const nonBetaUsers = useMemo(() => {
    const betaUidSet = new Set(
      betaRows.map((b) => b.uid).filter(Boolean)
    );
    return users.filter((u) => !betaUidSet.has(u.id));
  }, [users, betaRows]);

  const counts = useMemo(() => {
    const registered = enriched.filter((r) => r.registered).length;
    const pending = enriched.length - registered;
    const welcomed = enriched.filter((r) => r.welcome_sent).length;
    return {
      beta: enriched.length,
      registered,
      pending,
      welcomed,
      users: users.length,
      outside: nonBetaUsers.length
    };
  }, [enriched, nonBetaUsers, users]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let base = enriched;
    if (viewFilter === 'registered') base = enriched.filter((r) => r.registered);
    else if (viewFilter === 'pending') base = enriched.filter((r) => !r.registered);
    else if (viewFilter === 'not_welcomed')
      base = enriched.filter((r) => !r.welcome_sent);

    if (needle) {
      base = base.filter(
        (r) =>
          r.email.includes(needle) ||
          (r.user?.tiktokUsername || '').toLowerCase().includes(needle)
      );
    }

    return applySort(base, sortDescriptor, {
      email: (r) => r.email,
      approved_at: (r) => r.approved_at || 0,
      welcome_sent: (r) => (r.welcome_sent ? 1 : 0),
      welcome_sent_at: (r) => r.welcome_sent_at || 0,
      registered: (r) => (r.registered ? 1 : 0),
      plan: (r) => r.user?.plan || 'zzz'
    });
  }, [enriched, viewFilter, search, sortDescriptor]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Beta</h1>
          <p className="text-sm text-default-500">
            Cruce de la tabla D1 <code>beta_emails</code> con los usuarios
            registrados en Firebase.
          </p>
        </div>
        <Button
          size="sm"
          variant="flat"
          onPress={loadAll}
          isLoading={loading}
        >
          Refrescar
        </Button>
      </div>

      {/* Tarjetas de stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="En beta_emails" value={counts.beta} />
        <StatCard
          label="Registrados"
          value={counts.registered}
          sub={
            counts.beta > 0
              ? `${Math.round((counts.registered / counts.beta) * 100)}% de la beta`
              : undefined
          }
        />
        <StatCard
          label="Pendientes"
          value={counts.pending}
          sub="aprobados pero sin cuenta"
        />
        <StatCard
          label="Welcome enviado"
          value={counts.welcomed}
          sub={`${counts.beta - counts.welcomed} sin enviar`}
        />
        <StatCard
          label="Usuarios fuera de beta"
          value={counts.outside}
          sub={`total users: ${counts.users}`}
        />
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      {lookupError && (
        <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
          Cruce con Firebase Auth falló ({lookupError}). Los estados
          "Registrado/Pendiente" pueden no ser fiables.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por email o username…"
          value={search}
          onValueChange={setSearch}
          isClearable
          size="sm"
          className="max-w-sm"
        />
        <Tabs
          size="sm"
          selectedKey={viewFilter}
          onSelectionChange={(k) => setViewFilter(String(k))}
          aria-label="Filtrar"
        >
          <Tab key="all" title={`Todos (${counts.beta})`} />
          <Tab key="registered" title={`Registrados (${counts.registered})`} />
          <Tab key="pending" title={`Pendientes (${counts.pending})`} />
          <Tab key="not_welcomed" title={`Sin welcome (${counts.beta - counts.welcomed})`} />
        </Tabs>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <Table
          aria-label="Beta emails"
          isStriped
          removeWrapper
          classNames={{ th: 'bg-default-100' }}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        >
          <TableHeader>
            <TableColumn key="email" allowsSorting>Email</TableColumn>
            <TableColumn key="registered" allowsSorting>Estado</TableColumn>
            <TableColumn key="plan" allowsSorting>Usuario</TableColumn>
            <TableColumn key="approved_at" allowsSorting>Aprobado</TableColumn>
            <TableColumn key="welcome_sent" allowsSorting>Welcome</TableColumn>
            <TableColumn key="welcome_sent_at" allowsSorting>Welcome at</TableColumn>
            <TableColumn align="end">Acciones</TableColumn>
          </TableHeader>
          <TableBody emptyContent="Sin resultados.">
            {filtered.map((r) => (
              <TableRow key={r.email}>
                <TableCell>
                  <span>{isViewer ? maskEmail(r.email) : r.email}</span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {r.registered ? (
                      <Chip size="sm" variant="flat" color="success">
                        Registrado
                      </Chip>
                    ) : (
                      <Chip size="sm" variant="flat" color="warning">
                        Pendiente
                      </Chip>
                    )}
                    {r.providers && r.providers.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {r.providers.map((p) => (
                          <Chip key={p} size="sm" variant="flat" className="text-[10px]">
                            {providerLabel(p)}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {r.user ? (
                    <div className="flex flex-col">
                      {r.user.tiktokUsername && (
                        <span className="text-xs text-primary">
                          @{isViewer ? maskHandle(r.user.tiktokUsername) : r.user.tiktokUsername}
                        </span>
                      )}
                      <Chip size="sm" variant="flat">
                        {(r.user.plan || 'free').replace(/_/g, ' ')}
                      </Chip>
                    </div>
                  ) : (
                    <span className="text-default-400">—</span>
                  )}
                </TableCell>
                <TableCell>{formatTs(r.approved_at)}</TableCell>
                <TableCell>
                  {r.welcome_sent ? (
                    <Chip size="sm" variant="flat" color="success">
                      Enviado
                    </Chip>
                  ) : (
                    <Chip size="sm" variant="flat" color="default">
                      Pendiente
                    </Chip>
                  )}
                </TableCell>
                <TableCell>{formatTs(r.welcome_sent_at)}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    {r.user && canWrite && (
                      <Button
                        size="sm"
                        variant="flat"
                        onPress={() =>
                          navigate(`/users/${encodeURIComponent(r.user.id)}`)
                        }
                      >
                        Ver perfil
                      </Button>
                    )}
                    {!canWrite && (
                      <span className="text-xs text-default-400">solo lectura</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Usuarios fuera de beta */}
      {!loading && nonBetaUsers.length > 0 && (
        <details className="rounded-lg border border-default-200 bg-content1 p-4">
          <summary className="cursor-pointer text-sm font-medium">
            Usuarios registrados que NO están en beta_emails ·{' '}
            <span className="text-default-500">{nonBetaUsers.length}</span>
          </summary>
          <p className="mt-2 text-xs text-default-500">
            Son cuentas Firebase con plan activo u otras que nunca pasaron por
            la aprobación de beta (o se aprobaron antes de que existiera la
            tabla).
          </p>
          <Table aria-label="Usuarios fuera de beta" removeWrapper className="mt-3">
            <TableHeader>
              <TableColumn>Email</TableColumn>
              <TableColumn>TikTok</TableColumn>
              <TableColumn>Plan</TableColumn>
              <TableColumn align="end">Acciones</TableColumn>
            </TableHeader>
            <TableBody>
              {nonBetaUsers.slice(0, 100).map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{isViewer ? maskEmail(u.email) : u.email}</TableCell>
                  <TableCell>
                    {u.tiktokUsername
                      ? `@${isViewer ? maskHandle(u.tiktokUsername) : u.tiktokUsername}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="sm" variant="flat">
                      {(u.plan || 'free').replace(/_/g, ' ')}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      {canWrite ? (
                        <Button
                          size="sm"
                          variant="flat"
                          onPress={() =>
                            navigate(`/users/${encodeURIComponent(u.id)}`)
                          }
                        >
                          Ver perfil
                        </Button>
                      ) : (
                        <span className="text-xs text-default-400">solo lectura</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {nonBetaUsers.length > 100 && (
            <p className="mt-2 text-xs text-default-400">
              Mostrando los primeros 100 de {nonBetaUsers.length}.
            </p>
          )}
        </details>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-default-200 bg-content1 p-4">
      <div className="text-xs uppercase tracking-wider text-default-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-default-500">{sub}</div>}
    </div>
  );
}
